/**
 * The words for the reason codes an app writes — `GET /assortment/reason-labels`, `GET /launch/initial/reason-labels` (bata-platform
 * ADR 0057: the words are the platform's, in the caller's language, with the catalogue key beside the text).
 *
 * Both apps publish the same shape: `groups` keyed by where a code is written (`lineReason`, `gapReason`, `leftOutReason`…), each a
 * list of `{ code, label: { key, text } }`. A client that carried its own wording for every one of them would be a second source of
 * text for something the platform already says in three languages (it did, until this module: two reason catalogues in the UI).
 *
 *   `const reasons = useReasonLabels(assortmentReadReasonLabels)`   then   `reasons.labelOf('lineReason', code)`
 *
 * **What a code the platform does not label reads as.** `labelOrNull` is `null` — the caller says «reason not described in this
 * version» and keeps the code behind «id» (ADR 9060), as it does for any code it has no words for. `labelOf` is the code itself, for a
 * place with no room for that sentence. While the read is loading nothing is labelled yet, which is why a page does not draw a
 * reason's words until `loaded` (a code flashing in a table is a defect, R3).
 *
 * **In a file** (`readReasonLabels`): one read on a click, never a failure of the file — the code stands where no label came. The CSV
 * keeps the code (ADR 9058); the workbook writes the words (`reasonDisplay`).
 */

import { useMemo } from 'react';

import type { ReasonVocabulary } from './generated';
import { callOperation } from './http';
import { useApiQuery } from './hooks';
import type { OperationSpec } from './generated/operations';
import { SESSION_LONG_READ } from './session-cache';

/** An operation that answers a {@link ReasonVocabulary} and takes no parameters. */
export type ReasonLabelsOperation = OperationSpec<Record<string, never>, ReasonVocabulary>;

export interface ReasonLabels {
  /** Whether the read has settled — arrived, or failed. Until it has, a page draws no reason's words rather than a code. */
  readonly loaded: boolean;
  /** Whether the read failed: no code has words, and a page says so in one sentence instead of a code. */
  readonly failed: boolean;
  /** The platform's label of `code` in `group`, or `null` where it has none. */
  readonly labelOrNull: (group: string, code: string | null | undefined) => string | null;
  /** The platform's label, or the code itself where there is none. `''` for no code. */
  readonly labelOf: (group: string, code: string | null | undefined) => string;
  /** The codes of `group`, in the order the platform lists them. */
  readonly codes: (group: string) => readonly string[];
}

/** The labels of a vocabulary, as a lookup. A group or a code the platform does not name is simply absent. Pure. */
export function reasonLabelsFrom(vocabulary: ReasonVocabulary | undefined, settled: boolean = vocabulary !== undefined, failed = false): ReasonLabels {
  const groups = new Map<string, Map<string, string>>();
  const order = new Map<string, string[]>();
  const published = vocabulary !== undefined && typeof vocabulary.groups === 'object' && vocabulary.groups !== null ? vocabulary.groups : {};
  for (const [group, entries] of Object.entries(published)) {
    if (!Array.isArray(entries)) continue;
    const byCode = new Map<string, string>();
    for (const entry of entries as ReasonVocabulary['groups'][string]) {
      const text = entry?.label?.text;
      if (typeof entry?.code === 'string' && typeof text === 'string' && text.trim() !== '') byCode.set(entry.code, text);
    }
    groups.set(group, byCode);
    order.set(group, (entries as ReasonVocabulary['groups'][string]).filter((entry) => typeof entry?.code === 'string').map((entry) => entry.code));
  }
  const labelOrNull = (group: string, code: string | null | undefined): string | null => (
    code === null || code === undefined || code === '' ? null : groups.get(group)?.get(code) ?? null
  );
  return {
    loaded: settled,
    failed,
    labelOrNull,
    labelOf: (group, code) => (code === null || code === undefined ? '' : labelOrNull(group, code) ?? code),
    codes: (group) => order.get(group) ?? [],
  };
}

/** No labels: every code stands for itself. */
export const NO_REASON_LABELS: ReasonLabels = reasonLabelsFrom(undefined);

/** The read failed: settled, with no words for any code. */
export const FAILED_REASON_LABELS: ReasonLabels = reasonLabelsFrom(undefined, true, true);

/**
 * The labels of an app's reason codes, read once for the session (the words change with a release, not within a visit) and in the
 * background: a page never waits on them to draw, it draws a reason's words when they have come.
 */
export function useReasonLabels(operation: ReasonLabelsOperation): ReasonLabels {
  const read = useApiQuery(operation, {}, { ...SESSION_LONG_READ });
  const vocabulary = read.data;
  const settled = !read.isPending;
  const failed = read.isError;
  return useMemo(() => reasonLabelsFrom(vocabulary, settled, failed), [vocabulary, settled, failed]);
}

/**
 * The labels read outside React, for a file built on a click. **Never rejects**: a failure is {@link FAILED_REASON_LABELS}, so the code
 * stands where no word came and the file is still produced.
 */
export async function readReasonLabels(operation: ReasonLabelsOperation, language?: string): Promise<ReasonLabels> {
  try {
    return reasonLabelsFrom(await callOperation(operation, {}, language === undefined ? {} : { language }));
  } catch {
    return FAILED_REASON_LABELS;
  }
}

/** A label column's `display` for the workbook: the platform's words for the code in `group`, else the code. */
export function reasonDisplay(labels: ReasonLabels, group: string): (code: string) => string {
  return (code) => labels.labelOf(group, code);
}
